import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { sentry } from '../../../../../sentry';
import { LocationEnvironmentApi } from '../../../../core/api/location-environment/location-environment.api';
import { CreateLocationEnvironmentInput, UpdateLocationEnvironmentInput } from '../../../../core/api/location-environment/location-environment.type';
import { AuthenticationService } from '../../../../core/authentication/authentication.service';
import { CustomMessagesService } from '../../../../core/errors/custom-messages.service';
import { LocationEnvironment } from '../../../../core/interfaces/locations-environments';
import { log } from '../../../../core/utils/log';
import { locationEnvironmentNameUniqueAsyncValidator } from './dialog-location-environment.validator';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface DialogLocationEnvironmentData {
  locationEnvironment: LocationEnvironment;
}

export interface DialogLocationEnvironmentResult {
  reload?: boolean;
}

@Component({
  templateUrl: './dialog-location-environment.component.html',
  styleUrls: ['./dialog-location-environment.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogLocationEnvironmentComponent implements OnInit {

  public isUpdate = false;
  public isLoading = false;
  public locationEnvironmentForm: FormGroup;
  public isViewMode = !this.authenticationService.hasPermission('locations-edit');

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogLocationEnvironmentData,
    private readonly formBuilder: FormBuilder,
    private readonly translationLoader: FuseTranslationLoaderService,
    private readonly locationEnvironmentApi: LocationEnvironmentApi,
    private readonly messageService: CustomMessagesService,
    private readonly matDialogRef: MatDialogRef<DialogLocationEnvironmentComponent>,
    private readonly authenticationService: AuthenticationService
  ) {
    this.translationLoader.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.locationEnvironmentForm = this.formBuilder.group({
      name: new FormControl(this.data.locationEnvironment.name, {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [
          locationEnvironmentNameUniqueAsyncValidator({
            locationSectorId: this.data.locationEnvironment.locationSector && this.data.locationEnvironment.locationSector.id,
            locationEnvironmentId: this.data.locationEnvironment.id,
            locationEnvironmentApi: this.locationEnvironmentApi
          })
        ]
      }),
      location: new FormControl(this.data.locationEnvironment.location, [Validators.required]),
      locationSector: new FormControl(this.data.locationEnvironment.locationSector, [Validators.required]),
      description: [this.data.locationEnvironment.description, [Validators.maxLength(2000)]],
      qrCode: [this.data.locationEnvironment.qrCode, [Validators.required]]
    });

    this.locationEnvironmentForm.get('location').disable();
    if (this.data.locationEnvironment.locationSector) {
      this.locationEnvironmentForm.get('locationSector').disable();
    }

    if (this.isViewMode) {
      this.locationEnvironmentForm.disable();
    }
  }

  async save(): Promise<void> {
    try {
      this.isLoading = true;
      const locationEnvironment = this.data.locationEnvironment.id
        ? await this.locationEnvironmentApi.update(this.prepareUpdateInput())
        : await this.locationEnvironmentApi.create(this.prepareCreateInput());

      this.messageService.success('LOCATION_ENVIRONMENT_DIALOG.MESSAGE.SUCCESS');

      this.matDialogRef.close({ reload: true, locationEnvironment });
    } catch (error) {
      console.error(error);
      sentry.captureException(error);
      this.messageService.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  private prepareCreateInput(): CreateLocationEnvironmentInput {
    const form = this.locationEnvironmentForm.getRawValue();
    return {
      name: form.name,
      description: form.description,
      qrCode: form.qrCode,
      locationSectorId: form.locationSector.id
    };
  }

  private prepareUpdateInput(): UpdateLocationEnvironmentInput {
    const form = this.locationEnvironmentForm.getRawValue();
    return {
      id: this.data.locationEnvironment.id,
      name: form.name,
      description: form.description,
    };
  }

}
